import dayjs from 'dayjs';
import dynamic from 'next/dynamic';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { formatAmount } from '~/components/amount';
import { Button } from '~/components/button';
import { Box } from '~/components/layout';
import { useModal } from '~/components/modal';
import { Time } from '~/components/time';
import { Meta, Text } from '~/components/typography';
import { useToasts } from '~/context/toasts';
import { useResendReceiptMutation } from '~/graphql/resend-receipt';
import { colors } from '~/styles/colors';
import { DonationRecurrenceInterval, UtmMedium } from '~/types/graphql';
import { accessToken } from '~/utils/access-token';

import { CurrentUserOrdersQuery } from '../../graphql/current-user-orders';
import { IconCard, IconFile, IconSend, IconStop, IconWarning } from '~/components/icons';
import { useDonorPortalCancelRecurringDonationMutation } from './graphql/donor-portal-cancel-monthly-donation';
import { useDonorPortalRestartRecurringDonationMutation } from './graphql/donor-portal-restart-monthly-donation';
import { ButtonLink } from './style';
import { formatPaymentDate } from '~/utils/order-utils';

const ModalConfirm = dynamic(() => import('~/components/modal-confirm'));
const ModalReceipts = dynamic(() => import('./components/modal-receipts'));
const ModalUpdateDonation = dynamic(
  () => import('./components/ModalUpdateDonation')
);
const ModalUpdatePaymentMethod = dynamic(
  () => import('~/components/donor-portal/ModalUpdatePaymentMethod')
);
const ModalGiveMonthly = dynamic(
  () => import('./components/modal-give-monthly')
);

type Order = CurrentUserOrdersQuery['currentUserOrders'][0];

type Props = {
  order: Order;
};

export const Order = ({ order }: Props) => {
  const toasts = useToasts();
  const [resendingId, setResendingId] = useState<string>();

  const [resendReceiptMutation] = useResendReceiptMutation({
    context: {
      headers: {
        Authorization: `Bearer ${accessToken.get('donor-portal')}`
      }
    }
  });
  const [cancelMonthlyDonation, { loading: cancelRecurringDonationLoading }] =
    useDonorPortalCancelRecurringDonationMutation({
      context: {
        headers: {
          Authorization: `Bearer ${accessToken.get('donor-portal')}`
        }
      }
    });
  const [restartMonthlyDonation, { loading: restartRecurringDonationLoading }] =
    useDonorPortalRestartRecurringDonationMutation({
      context: {
        headers: {
          Authorization: `Bearer ${accessToken.get('donor-portal')}`
        }
      }
    });

  const modalReceipts = useModal();
  const modalUpdateDonation = useModal();
  const modalUpdatePaymentMethod = useModal();
  const modalCancelOrder = useModal();
  const modalGiveMonthly = useModal();
  const modalRestartOrder = useModal();

  const resendReceipt = async (id: string) => {
    setResendingId(id);
    await resendReceiptMutation({
      variables: {
        input: {
          transactionId: id
        }
      }
    });
    setResendingId(undefined);
    toasts.success('Receipt was sent to your email');
  };

  const isOffline = order.utm?.medium === UtmMedium.Offline;
  const isRecurring =
    order.recurrenceInterval != DonationRecurrenceInterval.None;
  const isCanceled = order.canceled;
  const restartChargesToday =
    !order.nextPayment || dayjs(order.nextPayment).isBefore(dayjs());

  return (
    <>
      <div className="overflow-hidden border border-whiteFog rounded-sm items-center">
        {order.paymentFailing && (
          <div className="px-5 py-3 flex items-center bg-error border-b border-whiteFog">
            <IconWarning className="size-5 text-white" />
            <Box marginLeft={16}>
              <Text bold style={{ color: colors.white, lineHeight: 1 }}>
                Payment Failed
              </Text>
              <Meta color={colors.white} style={{ fontWeight: 300 }}>
                Please update your payment method to continue supporting the{' '}
                {order.page.nonprofit.name}.
              </Meta>
            </Box>
          </div>
        )}

        <div className="border-b border-whiteFog">
          <div className="flex justify-between items-start gap-2 px-4 md:px-6 lg:px-10 pt-4 lg:pt-6 pb-5">
            <Box>
              <span className="font-medium text sm:text-lg leading-4 md:leading-7">
                {order.page.title}
              </span>
              <Meta regular>to {order.page.nonprofit.name}</Meta>
            </Box>
            <Box style={{ textAlign: 'right' }}>
              <div className="text-xl md:text-2xl font-medium">
                {formatAmount(order.amount)}
              </div>
              <div className="">
                {isRecurring && (
                  <div className="font-normal text-xs sm:text-sm text-dark leading-none">
                    {order.recurrenceInterval}
                  </div>
                )}
              </div>
            </Box>
          </div>

          <div className="px-4 md:px-6 lg:px-10 pb-4 lg:pb-6 flex justify-between items-end">
            <Meta regular>
              <Time format="LL">{order.created}</Time>
            </Meta>

            {isRecurring && (
              <Meta regular>
                <span>{isCanceled ? 'Canceled:' : 'Next payment:'} </span>
                <span>
                  {isCanceled
                    ? dayjs(order.canceled).format('LL')
                    : formatPaymentDate(order.nextPayment)}
                </span>
              </Meta>
            )}
          </div>
        </div>

        <div className="flex p-2 lg:p-4 justify-end items-center bg-soft min-h-14 lg:min-h-[4rem]">
          {isRecurring ? (
            <div
              className={twMerge(
                'flex gap-4 md:gap-8',
                order.canceled ? '' : 'pr-2 md:pr-4'
              )}
            >
              <ButtonLink onClick={modalReceipts.show}>
                <IconFile className="size-5" />
                Receipts
              </ButtonLink>
              {order.canceled ? (
                order.canRestart && (
                  <Button size="small" onClick={modalRestartOrder.show}>
                    Restart
                  </Button>
                )
              ) : (
                <>
                  <ButtonLink onClick={modalUpdateDonation.show}>
                    <IconCard className="size-5" />
                    Update
                  </ButtonLink>
                  <ButtonLink onClick={modalCancelOrder.show}>
                    <IconStop className="size-5" />
                    Stop Donating
                  </ButtonLink>
                </>
              )}
            </div>
          ) : (
            <div
              className={twMerge(
                'flex gap-4 md:gap-8',
                !isOffline ? '' : 'pr-2 md:pr-4'
              )}
            >
              <ButtonLink
                disabled={!!resendingId || order.receipts.length === 0}
                onClick={() => resendReceipt(order.receipts[0].id)}
              >
                <IconSend className="size-5" />
                {order.receipts.length > 0 &&
                resendingId === order.receipts[0].id
                  ? 'Sending'
                  : 'Resend Receipt'}
              </ButtonLink>
              {!isOffline && (
                <Button size="small" onClick={modalGiveMonthly.show}>
                  Give Monthly
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      <ModalReceipts
        receipts={order.receipts}
        resendReceipt={resendReceipt}
        resendingId={resendingId}
        {...modalReceipts}
      />
      <ModalUpdateDonation
        order={order}
        // reakit stacks dialogs poorly, so swap one modal for the other.
        onUpdatePaymentMethod={() => {
          modalUpdateDonation.hide();
          modalUpdatePaymentMethod.show();
        }}
        {...modalUpdateDonation}
      />
      <ModalUpdatePaymentMethod order={order} {...modalUpdatePaymentMethod} />
      <ModalGiveMonthly order={order} {...modalGiveMonthly} />
      <ModalConfirm
        heading="Stop Recurring Donation"
        message={`Are you sure you want to stop supporting ${order.page.nonprofit.name}?`}
        note={`${order.page.nonprofit.name} will stop receiving your recurring donations immediately.`}
        submitLabel="Stop Donating"
        danger
        onSubmit={async hide => {
          await cancelMonthlyDonation({
            variables: {
              input: {
                orderId: order.id
              }
            }
          });
          hide();
        }}
        loading={cancelRecurringDonationLoading}
        {...modalCancelOrder}
      />
      <ModalConfirm
        heading="Restart Recurring Donation"
        message={`You will restart your recurring support of ${order.page.nonprofit.name}.`}
        note={
          restartChargesToday
            ? 'Your payment method will be charged today, then on your regular schedule.'
            : `Your next payment will be on ${formatPaymentDate(order.nextPayment)}. You will not be charged today.`
        }
        submitLabel="Restart"
        onSubmit={async hide => {
          await restartMonthlyDonation({
            variables: {
              input: {
                orderId: order.id
              }
            }
          });
          hide();
        }}
        loading={restartRecurringDonationLoading}
        {...modalRestartOrder}
      />
    </>
  );
};
